(ns manatee.components.hybrid-part-answer
  "Specifically for the New Linguaskill Business project, this component
   consists of a merged part and answers screen on which a part and its
   associated answers is displayed.
   There is accompanying specific submission handling."
  (:require
   [cljs.pprint :as pprint]
   [manatee.annotation.autosaving :as autosaving]
   [manatee.annotation.components :as annotation-components]
   [manatee.annotation.handlers :as handlers]
   [manatee.annotation.subs :as annotation-subs]
   [manatee.components.answer :as answer-components]
   [manatee.components.editors.component :as editor-components]
   [manatee.components.question-text :as question-text-components]
   [manatee.domain.item :as item]
   [manatee.domain.task :as task]
   [manatee.keyboard.handlers :as keyboard]
   [manatee.spec.core :as spec]
   [manatee.ui.subs :as ui-subs]
   [re-frame.core :as rf]
   [rothko.accordion.component :as accordion-component]
   [rothko.audio.mini-player.component :as mini-player-component]
   [rothko.audio.wave-player.component :as wave-player-component]
   [rothko.component.core :refer [defcomp]]
   [rothko.media.component :as media-component]
   [rothko.panel.component :as panel-component]))

(defn- waveplayer-component
  [{:keys [id on-destroy-handler on-finish-handler on-pause-handler on-play-handler on-ready-handler on-error-handler
           on-load-handler rubric text source-url segment-annotation-field time-markers-field autosaving-disabled?
           item]}]
  (let [question (:question item)
        title [accordion-component/accordion
               {:appearance #{:primary}
                :open #{:question}
                :items [(when (seq text)
                          {:key :question
                           :title [:h6 "Question"]
                           :content [:<>
                                     [:div.default-style-in-markdown
                                      (question-text-components/with-markdown text)]
                                     (when (:audio-url question)
                                       [mini-player-component/mini-player {:appearance #{:align-left}
                                                                           :src (:audio-url question)
                                                                           :label "Listen to the question"}])
                                     (when (:image-urls question)
                                       [:div
                                        (for [image-url (:image-urls question)]
                                          ^{:key image-url}
                                          [media-component/media {:type :image
                                                                  :src image-url}])])]})]}]
        event-opts {:item item
                    :autosaving-disabled? autosaving-disabled?}
        playhead-autosaving? (and segment-annotation-field
                                  (not autosaving-disabled?))]
    ^{:key id}
    [wave-player-component/wave-player
     {:allow-playback-rate-change? (not (nil? segment-annotation-field)) ; Only for transcript-annotation tasks.
      :appearance (into #{:primary} (when segment-annotation-field [:diarisation]))
      :audio-src source-url
      :component-did-mount #(keyboard/make-init-waveplayer id %)
      :component-will-unmount #(rf/dispatch [::keyboard/clear-keyboard])
      :id id
      :on-destroy-handler on-destroy-handler
      :on-finish-handler on-finish-handler
      :on-load-handler on-load-handler
      :on-pause-handler on-pause-handler
      :on-play-handler on-play-handler
      :on-ready-handler on-ready-handler
      :on-error-handler on-error-handler
      :options {:cursor-time {:hideOnBlur true
                              :hideOnScroll false}}
      :plugins (into [:cursor-time] (when segment-annotation-field [:regions]))
      :title title
      :regions (when segment-annotation-field
                 @(rf/subscribe [::annotation-subs/segments id]))
      :reload-regions? (when (and segment-annotation-field
                                  @(rf/subscribe [::annotation-subs/reload-segments? id]))
                         (rf/dispatch [::handlers/reset-reload-segments id])
                         true)
      :on-regions-change (when segment-annotation-field
                           #(rf/dispatch [::handlers/segments-changed event-opts %]))
      :region-id-to-select (when segment-annotation-field
                             @(rf/subscribe [::annotation-subs/segment-id-to-select id]))
      :colour-palette (:speakers segment-annotation-field)
      :colour-id-key :speaker
      :time-markers (when time-markers-field
                      @(rf/subscribe [::annotation-subs/time-markers id]))
      :on-time-markers-change (when time-markers-field
                                #(rf/dispatch [::handlers/time-markers-changed event-opts %]))
      :time-marker-types (:time-marker-types time-markers-field)
      :playhead-position (when playhead-autosaving?
                           @(rf/subscribe [::autosaving/autosaved-playhead id]))
      :on-playhead-move #(do (rf/dispatch [::editor-components/update-playhead %])
                             (when playhead-autosaving?
                               (rf/dispatch [::autosaving/autosave-playhead item %])))
      :title-prefix rubric}]))

(defcomp answer-waveplayer
  {:spec ::spec/answer-waveplayer}
  [{{{{part-number :number} :part
      :keys [text]} :question
     :keys [source-url id]} :item
    :keys [waveplayer item task-config autosaving-disabled?]
    :as _props}
   & _children] ; required by defcomp
  (let [{:keys [account-ids]} @(rf/subscribe [::ui-subs/config])]
    (waveplayer-component {:id id
                           :on-destroy-handler (:on-destroy waveplayer)
                           :on-finish-handler (:on-finish waveplayer)
                           :on-load-handler (:on-load waveplayer)
                           :on-pause-handler (:on-pause waveplayer)
                           :on-play-handler (:on-play waveplayer)
                           :on-ready-handler (:on-ready waveplayer)
                           :on-error-handler (:on-error waveplayer)
                           :part-number part-number
                           :account (task/account task-config account-ids)
                           :text text
                           :source-url source-url
                           :segment-annotation-field (task/segment-annotation-field task-config)
                           :time-markers-field (task/time-markers-field task-config)
                           :autosaving-disabled? autosaving-disabled?
                           :item item})))

(defn- answer-panel
  [{:keys [item] :as props} & child-components]
  (let [audio-length (get item :audio-length-seconds)
        speaking-time (get-in item [:question :speaking-time])
        audio-ready-event-handler (answer-components/audio-event-with-duration-handler [::handlers/hybrid-part-answer-audio-ready item])
        audio-error-event-handler (answer-components/audio-event-handler [::handlers/submit-hybrid-part-answer-annotation item])
        props (-> props
                  ; Add handlers for the wave-player `on-ready` and `on-error` handlers. These trigger the submission
                  ; of an edit for the answer without annotator intervention. This marks the answer as edited so that
                  ; the part edit can be submitted (or rejected).
                  (assoc-in [:waveplayer :on-ready] audio-ready-event-handler)
                  (assoc-in [:waveplayer :on-error] audio-error-event-handler))]
    (-> [panel-component/panel {:appearance #{:primary}
                                #_#_:class #{:allow-overflow}}
         [:<>
          [:span
           [:h5 (get-in item [:question :question-number])]
           [answer-waveplayer props]]
          (when (and audio-length speaking-time)
            [:p (pprint/cl-format nil "Audio duration: ~$ / ~$s" audio-length speaking-time)])]]
        (into child-components))))

(defn part+answers-panel
  [{:keys [answers class task-config]
    {:keys [task-documents]
     {:keys [number short-rubric rubric]} :part :as _item} :item}
   & child-components]
  (-> [panel-component/panel (cond-> {:appearance #{:primary}}
                               class (assoc :class class))
       (when (seq task-documents)
         (annotation-components/info-links task-documents
                                           (:id task-config)))
       [:h4 (item/part-label number
                             (task/task-config->part-description task-config number))]
       (let [rubric? (task/modifier task-config :show-long-rubric?)]
         (when (and rubric? rubric)
           [accordion-component/accordion {:appearance #{:primary}
                                           :open #{:rubric}
                                           :items [{:key :rubric
                                                    :title [:h6 "Stimulus"]
                                                    :content [:div.default-style-in-markdown
                                                              (question-text-components/with-markdown rubric)]}]}]))
       (let [short-rubric? (task/modifier task-config :show-short-rubric?)]
         (when (and short-rubric? short-rubric)
           [accordion-component/accordion {:appearance #{:primary}
                                           :open #{:short-rubric}
                                           :items [{:key :short-rubric
                                                    :title [:h6 "Functional Competency Descriptors"]
                                                    :content [:div.default-style-in-markdown
                                                              (question-text-components/with-markdown short-rubric)]}]}]))]
      (into (for [answer answers]
              [answer-panel (answer-components/make-answer-props answer task-config false {})]))
      (into child-components)))
